import { app } from "@microsoft/teams-js";
import {
  createNestablePublicClientApplication,
  createStandardPublicClientApplication,
  InteractionRequiredAuthError,
  LogLevel,
  type Configuration,
  type IPublicClientApplication,
} from "@azure/msal-browser";
import { CLIENT_ID, TENANT_ID } from "./config";

// ── Anmeldung ────────────────────────────────────────────────────────────────
// EINE getToken(), die Umgebung entscheidet nur über das WIE der Interaktion.
//
//   In Teams   → Nested App Authentication. Der Host vermittelt die Token.
//                Kein Server-Teil, kein Client-Geheimnis. Muss Interaktion
//                sein, dann als Fenster: Weiterleitungen sind in Rahmen
//                (iframes) nicht unterstützt, und ein Teams-Tab IST ein Rahmen.
//   Im Browser → gewöhnliche MSAL-Anmeldung per WEITERLEITUNG. Kein Fenster,
//                also kein Pop-up-Blocker, kein verwaistes Fenster und keine
//                hängende Markierung "es läuft schon eine Anmeldung".
//
// REIHENFOLGE ZÄHLT: TeamsJS muss VOR MSAL initialisiert werden, sonst
// erkennt MSAL den Host nicht und fällt still auf den Browser-Weg zurück.

let pca: IPublicClientApplication | null = null;
let laueftInTeams = false;

// Protokoll für die Fehlersuche. Wird auf der Seite selbst angezeigt,
// damit man keine Entwicklerwerkzeuge braucht.
export const protokoll: string[] = [];

const msalConfig: Configuration = {
  auth: {
    clientId: CLIENT_ID,
    authority: `https://login.microsoftonline.com/${TENANT_ID}`,
    redirectUri: window.location.origin,
  },
  cache: { cacheLocation: "localStorage" },
  system: {
    loggerOptions: {
      // NUR Warnungen und Fehler. Auf Stufe Info schreibt MSAL pro Start rund
      // 50 nichtssagende Zeilen (Korrelations-Kennungen) – bei 60 Plätzen im
      // Puffer hätte das unsere EIGENEN Zeilen hinausgedrängt, und genau die
      // sagen im Fehlerfall, in welcher Umgebung die App lief. Ein Protokoll,
      // das sich selbst überschreibt, ist keins.
      logLevel: LogLevel.Warning,
      piiLoggingEnabled: false,
      loggerCallback: (_stufe, nachricht) => {
        protokoll.push(nachricht);
        if (protokoll.length > 60) protokoll.shift();
      },
    },
  },
};

// initAuth darf beliebig oft aufgerufen werden und tut die Arbeit trotzdem
// nur EINMAL. React ruft Effekte im Entwicklungsmodus absichtlich doppelt
// auf; ohne diese Sperre entstünden zwei MSAL-Instanzen und zwei
// gleichzeitige Anmeldungen.
let initLauft: Promise<{ inTeams: boolean }> | null = null;

export function initAuth(): Promise<{ inTeams: boolean }> {
  initLauft ??= initAuthWirklich();
  return initLauft;
}

async function initAuthWirklich(): Promise<{ inTeams: boolean }> {
  try {
    await app.initialize();
    laueftInTeams = true;
  } catch {
    // Kein Fehler, sondern der andere Fall: normaler Browser statt Teams.
    laueftInTeams = false;
  }

  // Die nestbare Instanz gehört in einen Host, der sie vermitteln kann.
  // Im normalen Browser gibt es keinen Vermittler.
  pca = laueftInTeams
    ? await createNestablePublicClientApplication(msalConfig)
    : await createStandardPublicClientApplication(msalConfig);

  // PFLICHT, auch wenn gerade keine Weiterleitung lief: Dieser Aufruf holt
  // das Ergebnis einer Anmelde-Weiterleitung ab UND räumt eine
  // hängengebliebene Markierung "es läuft schon eine Anmeldung" weg.
  // Fehlt er, scheitert jeder weitere Versuch mit interaction_in_progress,
  // sobald einmal eine Anmeldung abgebrochen wurde.
  const rueckkehr = await pca.handleRedirectPromise();
  if (rueckkehr?.account) {
    pca.setActiveAccount(rueckkehr.account);
    protokoll.push("[Start] Von der Anmelde-Weiterleitung zurückgekehrt");
  }

  protokoll.push(
    `[Start] Umgebung: ${laueftInTeams ? "Teams (nestbar/NAA)" : "Browser (gewöhnlich)"}`,
  );
  return { inTeams: laueftInTeams };
}

// Meldet nur IN DIESER APP ab: MSALs lokaler Zwischenspeicher wird geleert,
// die Microsoft-Sitzung im Browser bleibt bestehen. Beim naechsten Laden
// beginnt die Anmeldung also von vorn, ohne dass man sich aus allen anderen
// Microsoft-Diensten ausloggt. Bewusst NICHT logoutRedirect - das wuerde
// die Sitzung browserweit beenden.
export async function abmelden(): Promise<void> {
  if (!pca) return;
  await pca.clearCache();
  window.location.reload();
}

export async function getToken(scopes: readonly string[]): Promise<string> {
  if (!pca) throw new Error("initAuth() wurde nicht aufgerufen.");
  const konto = pca.getActiveAccount() ?? pca.getAllAccounts()[0];

  // Noch gar kein Konto bekannt? Kein Fehler, sondern der Normalfall beim
  // ersten Öffnen. Der stille Versuch würde hier mit `no_account_error`
  // scheitern – einem BrowserAuthError, NICHT einem
  // InteractionRequiredAuthError. Die Unterscheidung unten würde ihn
  // durchreichen und der Nutzer sähe eine Fehlermeldung statt einer
  // Anmeldung.
  if (!konto) return await anmeldenInteraktiv(scopes);

  try {
    const antwort = await pca.acquireTokenSilent({
      scopes: [...scopes],
      account: konto,
    });
    if (!pca.getActiveAccount()) pca.setActiveAccount(antwort.account);
    return antwort.accessToken;
  } catch (fehler) {
    // NUR ein echter Anmeldebedarf führt zur Interaktion.
    //
    // Ein Netzaussetzer, eine Zeitüberschreitung oder ein kurzzeitig nicht
    // erreichbarer Anmeldedienst sind VORÜBERGEHEND. Die alte Zeitleiste
    // behandelte jeden Fehler als "Anmeldung nötig" und warf die Nutzer
    // ganzseitig zu Microsoft – bei jedem Schluckauf. Alles andere fliegt
    // hier nach oben und wird gemeldet.
    if (!(fehler instanceof InteractionRequiredAuthError)) throw fehler;
    return await anmeldenInteraktiv(scopes);
  }
}

// Der einzige Weg, der den Nutzer behelligt. Bewusst nur von zwei Stellen
// aufgerufen: wenn es noch kein Konto gibt, und wenn MSAL ausdrücklich
// Interaktion verlangt.
async function anmeldenInteraktiv(scopes: readonly string[]): Promise<string> {
  if (!pca) throw new Error("initAuth() wurde nicht aufgerufen.");

  if (laueftInTeams) {
    // Im Rahmen sind Weiterleitungen nicht möglich – hier ist das Fenster
    // der vorgesehene Weg, und Teams vermittelt es.
    const antwort = await pca.acquireTokenPopup({ scopes: [...scopes] });
    pca.setActiveAccount(antwort.account);
    return antwort.accessToken;
  }

  // Im normalen Browser: Weiterleitung. Der Aufruf kehrt NICHT zurück –
  // die Seite wird verlassen und nach der Anmeldung neu geladen; dort holt
  // handleRedirectPromise() in initAuth das Ergebnis ab.
  protokoll.push("[Auth] Weiterleitung zur Microsoft-Anmeldung …");
  await pca.acquireTokenRedirect({ scopes: [...scopes] });
  throw new Error("Weiterleitung zur Anmeldung läuft …");
}
